import {ChangeDetectionStrategy, Component, forwardRef, Input, OnDestroy, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {ControlValueAccessor, FormControl, FormGroup, NG_VALUE_ACCESSOR, ReactiveFormsModule} from '@angular/forms';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatSelectModule} from '@angular/material/select';
import {MatInputModule} from '@angular/material/input';
import {Subject, takeUntil} from 'rxjs';
import * as countryCodes from 'country-codes-list';
import {CountryPhone} from "../../content/destinations/destinations.types";

@Component({
    selector: 'app-phone-input',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatSelectModule,
        MatInputModule,
    ],
    templateUrl: './phone-input.component.html',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => PhoneInputComponent),
            multi: true,
        },
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PhoneInputComponent implements ControlValueAccessor, OnInit, OnDestroy {
    @Input() readonly: boolean = false;

    phoneForm = new FormGroup({
        code: new FormControl('+1'),
        number: new FormControl(''),
    });

    countryCodes: CountryPhone[] = [];

    protected _onTouched: () => void = () => {};
    private _onChange: (value: string | null) => void = () => {};
    private _destroy$ = new Subject<void>();

    constructor() {
        this.countryCodes = countryCodes.all()
            .map(country => ({
                name: country.countryNameEn,
                code: `+${country.countryCallingCode}`,
                iso: country.countryNameEn
            }))
            .filter(country => country.code !== '+')
            .sort((a, b) => a.name.localeCompare(b.name));
    }

    ngOnInit(): void {
        this.phoneForm.valueChanges
            .pipe(takeUntil(this._destroy$))
            .subscribe(value => {
                const { code, number } = value;
                if (code && number) {
                    this._onChange(`${code} ${number}`);
                } else {
                    this._onChange(null);
                }
            });
    }

    ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
    }

    writeValue(value: string | null): void {
        if (value) {
            const foundCountry = this.countryCodes
                .slice()
                .sort((a, b) => b.code.length - a.code.length)
                .find(c => value.startsWith(c.code));

            if (foundCountry) {
                const number = value.substring(foundCountry.code.length).trim();
                this.phoneForm.setValue({ code: foundCountry.code, number: number }, { emitEvent: false });
            } else {
                this.phoneForm.setValue({ code: '+1', number: value }, { emitEvent: false });
            }
        } else {
            this.phoneForm.reset({ code: '+1', number: '' }, { emitEvent: false });
        }
    }

    registerOnChange(fn: any): void {
        this._onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this._onTouched = fn;
    }

    setDisabledState?(isDisabled: boolean): void {
        isDisabled ? this.phoneForm.disable() : this.phoneForm.enable();
        if (this.readonly) {
            this.phoneForm.disable();
        }
    }
}
